import React, { useState } from 'react';

import { Icon } from '../../components';
import { FromClipboard } from './FromClipboard';
import ColumnBasicFields from './ColumnBasicFields';
import ColumnStyleFields from './ColumnStyleFields';
import ColumnEnumValuesSection from './ColumnEnumValuesSection';
import { Section, EmptyMessage, Button } from '../../components';
import ColumnDefaultStyleFields from './ColumnDefaultStyleFields';
import ColumnCompositeFieldsSection from './ColumnCompositeFieldsSection';

type ColumnBuilderProps = {
  value?: DynamicColumnType[];
  onChange?: (columns: DynamicColumnType[]) => void;
};

const ColumnsBuilder: React.FC<ColumnBuilderProps> = ({
  value = [],
  onChange,
}) => {
  const [editingId, setEditingId] = useState<number | string | null>(null);

  const handleAddColumn = () => {
    const newColumn: DynamicColumnType = {
      id: crypto.randomUUID(),
      key: `col_${Date.now()}`,
      label: 'New Column',
    };
    const updatedColumns = [...value, newColumn];
    onChange?.(updatedColumns);
  };

  const handleRemoveColumn = (index: number) => {
    const updatedColumns = value.filter((_, i) => i !== index);
    onChange?.(updatedColumns);
  };

  const handleUpdateColumn = (
    index: number,
    updates: Partial<DynamicColumnType>
  ) => {
    const updatedColumns = value.map((col, i) =>
      i === index ? { ...col, ...updates } : col
    );
    onChange?.(updatedColumns);
  };

  const renderColumnEditor = (column: DynamicColumnType, index: number) => {
    const isEditing = editingId === column.id || editingId === index;
    const actions = (
      <div className="flex flex-row items-center justify-end gap-sm -mt-lg -mr-lg">
        <Button
          width="small"
          type="primary"
          icon={isEditing ? undefined : 'edit'}
          onClick={() => setEditingId(isEditing ? null : column.id || index)}
        >
          {isEditing ? 'Done' : ''}
        </Button>
        <Button
          width="small"
          type="danger"
          icon="delete"
          onClick={() => handleRemoveColumn(index)}
        />
      </div>
    );

    return (
      <Section
        key={column.id || index}
        actions={actions}
        title={column.label || 'Untitled Column'}
      >
        {/* Editing Mode */}
        {isEditing ? (
          <div className="flex flex-col gap-y-2xl">
            <Section subtitle="Configure basic column properties">
              <ColumnBasicFields
                column={column}
                onUpdate={updates => handleUpdateColumn(index, updates)}
              />
            </Section>

            <ColumnStyleFields
              style={column.style}
              onUpdate={style => handleUpdateColumn(index, { style })}
            />

            <ColumnDefaultStyleFields
              defaultStyle={column.default_style}
              onUpdate={defaultStyle =>
                handleUpdateColumn(index, { default_style: defaultStyle })
              }
            />

            {column.type === 'enum' && (
              <ColumnEnumValuesSection
                valueStyles={column.value_styles}
                onUpdate={valueStyles =>
                  handleUpdateColumn(index, { value_styles: valueStyles })
                }
              />
            )}

            {column.type === 'composite' && (
              <ColumnCompositeFieldsSection
                fields={column.fields}
                onUpdate={fields => handleUpdateColumn(index, { fields })}
              />
            )}
          </div>
        ) : (
          /* Preview Mode */
          <div className="flex flex-col gap-md text-small text-textLight">
            <div className="grid grid-cols-2 gap-md">
              <div>
                <span className="text-textLight font-medium">Type:</span>{' '}
                {column.type || 'text'}
              </div>
              {column.width && (
                <div>
                  <span className="text-textLight font-medium">Width:</span>{' '}
                  {column.width}
                </div>
              )}
              {column.sort_key && (
                <div>
                  <span className="text-textLight font-medium">Sort Key:</span>{' '}
                  {column.sort_key}
                </div>
              )}
              {column.null_label && (
                <div>
                  <span className="text-textLight font-medium">
                    Null Label:
                  </span>{' '}
                  {column.null_label}
                </div>
              )}
            </div>
            {column.sortable && <Icon icon="yes" label="Sortable" />}
            {column.style && <Icon icon="yes" label="Custom field style" />}
            {column.default_style && (
              <Icon icon="yes" label="Default style configured" />
            )}

            {column.value_styles &&
              Object.keys(column.value_styles).length > 0 && (
                <Icon
                  icon="yes"
                  label={`${Object.keys(column.value_styles).length} enum values`}
                />
              )}

            {column.fields && column.fields.length > 0 && (
              <Icon
                icon="yes"
                label={`${column.fields.length} composite fields`}
              />
            )}
          </div>
        )}
      </Section>
    );
  };

  return (
    <div className="flex flex-col gap-y-md">
      <div className="flex flex-row items-end justify-between">
        <h2 className="text-medium font-medium text-text">Columns</h2>
        <div className="flex flex-row items-center gap-x-md">
          <FromClipboard onChange={onChange} />
          <Button
            icon="plus"
            width="small"
            type="primary"
            onClick={handleAddColumn}
          >
            Add Column
          </Button>
        </div>
      </div>

      {value.length === 0 ? (
        <EmptyMessage>
          No columns defined. Click "Add Column" to get started.
        </EmptyMessage>
      ) : (
        <div className="flex flex-col gap-y-2xl">
          {value.map((column, index) => renderColumnEditor(column, index))}
        </div>
      )}
    </div>
  );
};

export default ColumnsBuilder;
